import { requestHttp } from '@/utils';

const SERVER = ``;

/**
 * 方案数据详情类型定义
 *
 * **接口文档** 黄冰洋<huangbingyang@lysoratech.com>
 *
 * [方案列表](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/listUsingGET_4) - https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/listUsingGET_4
 *
 * @typedef { object } SchemeData
 * @property { string } id 方案ID
 * @property { string } schemeName 方案名称
 * @property { string } sceneName 方案场景名称
 * @property { string } sceneId 方案场景ID
 * @property { string } sceneImage 方案场景图标链接地址
 * @property { `talking` | `unfinished` | `finished` } schemeStatus 方案状态标识
 * - `talking` - 谈单中
 * - `unfinished` - 未完单
 * - `finished` - 已完单
 * @property { string } schemeStatusName 方案状态文案，**注：服务端该字段未词条国际化，需前端自行通过 `schemeStatus` 字段映射**
 * @property { object } [planBg] 云地勘项目信息（图片等）
 * @property { boolean } [planBg.bridge] 是否网桥地勘
 * @property { string } [planBg.sourceBgUrl] 方案图片地址，可用于判断是否跳转云地勘
 * @property { boolean } hasOutdoorProd 是否有网桥地勘地位方案，可用于判断是否跳转网桥地勘
 * @property { boolean } hasProd 是否有清单产品，可用于判断是否跳转方案清单
 * @property { string } createTime 创建时间
 * @property { number | string } addMethod 添加方式，TODO: 目前添加方式有哪些值，分别代表什么意思不清楚？
 * @property { boolean } isValid 方案合法校验：光方案校验是否正确，true为校验通过
 * @property { boolean } ownScheme 是否属于自己的的方案
 * @property { 0 | 1 | 2 } shareType 分享状态
 * @property { string } shareUserName 方案分享者账号名
 * @property { 0 | 1 } currentUserPermission 当前用户权限（读写）
 * - `0` - 只读权限
 * - `1` - 可写权限
 * @property { string } remarks 备注
 * @property { boolean } isSurveyScheme 是否是地勘出的方案，即PC端创建的方案，`false` 时表示App端创建的方案，需调用 [schemeTransferApi](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/appSchemeToPcUsingGET) 转换
 */

/**
 * 请求生成Demo方案数据
 *
 * 主要用于初始化用户无方案数据时快速生成体验方案
 *
 * **接口文档**
 * @author 黄冰洋<huangbingyang@lysoratech.com>
 * @see https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/genEmuSchemeDemoUsingPOST
 *
 * @returns { Promise<string> } 返回方案ID
 */
export const requestCreateDemoScheme = async () => {
  const result = await requestHttp({
    api: `${SERVER}/scheme/emu/scheme`,
    method: 'POST',
    module: 'schemes'
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};

/**
 * 方案搜索请求参数类型定义
 *
 * **接口文档** - 黄冰洋<huangbingyang@lysoratech.com>
 *
 * - [方案列表](https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/listUsingGET_4) - https://enetdevtest.rj.link/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/listUsingGET_4
 *
 * @typedef { object } SchemeSearchParams
 * @property { number } pageIndex 页码
 * @property { number } pageSize 分页数量
 * @property { string } [fuzzyKey] 方案名称搜索关键字
 * @property { string } orderBy 排序字段名称，默认 `createTime` 按创建时间排序
 * @property { boolean } asc 是否升序或降序，默认 `false` 默认按创建时间从新到旧排序
 * @property { boolean } shareScheme 是否显示我接收的方案列表
 * @property { number | string } sceneId 场景ID
 * @property { boolean } fromPc PC端请求来源标识，默认 `true`
 */

/**
 * 方案列表统计结果类型定义
 * @typedef { object } SchemeListCountResult
 * @property { number } schemeCount 我创建的
 * @property { number } sharedSchemeCount 我接收的
 */

/**
 * 请求方案列表统计（我创建和我接收）
 *
 * **接口文档**
 * @author 黄冰洋<huangbingyang@lysoratech.com>
 * @see https://lysoratech-enet-test.rj.link:6443/api/v1/schemes/doc.html#/default/%E6%96%B9%E6%A1%88/countListSchemeUsingGET
 *
 * @param { SchemeSearchParams } params
 * @returns { Promise<SchemeListCountResult> } 返回我创建和我接收方案数据统计
 */
export const requestSchemeListCount = async params => {
  const result = await requestHttp({
    api: `${SERVER}/scheme/list/count`,
    method: 'GET',
    module: 'schemes',
    querys: params
  });

  if (result.code === 0) {
    return result.data;
  } else {
    return Promise.reject(result);
  }
};
